/**
 * @module experience/credit-check
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:max-file-line-count */
/* tslint:disable:no-any */ // Need to support all data types
// Core
import { SelectItem } from '@support/shared/interfaces/select-item.interface';
import { Router } from '@angular/router';
import { throwError as observableThrowError, Subject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// api endpoints
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { ValidatorFn, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';

// Service
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { LoggerService } from '@core/logger/services/logger.service';
import { CREDIT_CHECK_MODULE_COMPONENTS } from '@experience/shop/credit-check/constants/credit-check.constants';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
// Interface
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { AddressValidationRequest } from '@support/api/proxies/requests/api-2004.interface';
import {
  AddressValidationResponse,
  Address,
} from '@support/api/proxies/responses/api-2004.interface';
import { AemAuthorableContent } from '@support/shared/interfaces/aem-authorable-content.interface';
import { GetRiskAssessmentIdTypes } from '@support/api/proxies/responses/api-2002.interface';

// Constants
import { FIELD_VALIDATIONS_CONSTANTS } from '@support/shared/services/common-api/common-api.constants';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { Injectable, EventEmitter } from '@angular/core';
import { ContentProviderService } from '@core/aem/services/content-provider.service';

// Api endpoints
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';

// custom interfaces
import { APIError } from '@support/api/common/error.interface';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { API2003Request } from '@support/api/proxies/requests/api-2003.interface';
import { API2003Response } from '@support/api/proxies/responses/api-2003.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { BillingTypeEnum } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { CreditCheckResponse } from '@support/api/proxies/responses/api-2001.interface';
import { CreditCheckRequestModel } from '@experience/shop/credit-check/models/credit-check-request.model';
import { HttpResponse, HttpHeaders } from '@angular/common/http';

@Injectable()
export class CreditCheckService {
  /**
   * Store value of ssnRequired Emitter
   */
  public ssnRequiredEmitter: EventEmitter<boolean>;
  /**
   * stores the list of suffixes
   */
  public suffixes: AemAuthorableContent[];

  /**
   * Mock the AccountSubTypes data
   */
  public accountSubTypes: AemAuthorableContent[];

  /**
   * Object of type Browse entry used to store user state
   */
  public customerConfigData: BrowseEntry;
  /**
   * @TODO Added as convienence for client demo on 11/21 need to remove once done
   */
  public populateDemoData: Subject<boolean>;
  public populateDemoData$: Observable<boolean>;
  public selectedAddress: Subject<Address>;
  public selectedAddress$: Observable<Address>;
  public notifyAddressChange: Subject<boolean>;
  public notifyAddressChange$: Observable<boolean>;
  constructor(
    private contentProviderService: ContentProviderService,
    private apiGatewayService: ApiGatewayService,
    private formBuilder: UntypedFormBuilder,
    private formCreationService: FormCreationService,
    private commonApiService: CommonApiService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private router: Router,
    private datePipe: DatePipe,
    private logger: LoggerService,
    private objectService: ObjectService
  ) {
    this.ssnRequiredEmitter = new EventEmitter();
    this.accountSubTypes = [];
    this.suffixes = [];
    this.populateDemoData = new Subject<boolean>();
    this.populateDemoData$ = this.populateDemoData.asObservable();
    this.selectedAddress = new Subject<Address>();
    this.selectedAddress$ = this.selectedAddress.asObservable();
    this.notifyAddressChange = new Subject<boolean>();
    this.notifyAddressChange$ = this.notifyAddressChange.asObservable();
  }

  /**
   * Requests Common API service for SelectItem[] to get States drop down
   * @returns observable<SelectItem[]>
   */
  public getAllStates(): Observable<SelectItem[]> {
    return this.commonApiService.getStates('name', 'code');
  }

  /**
   * This is a reference data retrieval API to return the list of acceptable Identification
   * documents to run the credit check. This API is used to populate the
   * document type dropdown
   */
  public getriskAssessmentIdTypes(): Observable<GetRiskAssessmentIdTypes> {
    return this.apiGatewayService
      .getRequest(API_MAP.API_2002_riskAssessmentIdTypes, null, null, null)
      .pipe(
        map((response: HttpResponse<GetRiskAssessmentIdTypes>) => {
          this.logger.debug(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            'received risk assessment ID types for dropdown ' +
              CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            ' response ' + JSON.stringify(response)
          );
          return response.body;
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            ' API error for getRequest for getriskAssessmentIdTypes() in ' +
              CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );
          return observableThrowError(error);
        })
      );
  }

  /**
   * Builds the Name and Address form group for the credit check component
   * @returns FormGroup
   */
  public buildNameAddressForm(): UntypedFormGroup {
    const nameAddressFormGrp = this.formBuilder.group({
      firstName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.FIRST_NAME,
        '',
        true
      ),
      middleName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.MIDDLE_INITIAL,
        '',
        false
      ),
      lastName: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.LAST_NAME,
        '',
        true
      ),
      suffix: [null, []],
      accSubType: this.formCreationService.getFormControl('', '', true),
      addressLine1: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.ADDRESS_LINE1,
        '',
        true
      ),
      addressLine2: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.ADDRESS_LINE2,
        '',
        false
      ),
      city: this.formCreationService.getFormControl(FIELD_VALIDATIONS_CONSTANTS.CITY, '', true),
      state: ['', [Validators.required]],
      zip: this.formCreationService.getFormControl(FIELD_VALIDATIONS_CONSTANTS.ZIP_CODE, '', true),
    });
    return nameAddressFormGrp;
  }

  /**
   * Builds the Identification form group for the credit check component
   * @returns FormGroup
   */
  public buildIdentificationForm(): UntypedFormGroup {
    const identificationFormGrp = this.formBuilder.group({
      idType: ['', [Validators.required]],
      city: ['', [Validators.required]],
      idNumber: this.formCreationService.getFormControl('', '', true),
      birthDate: ['', [Validators.required]],
      ssn: this.formCreationService.getFormControl(FIELD_VALIDATIONS_CONSTANTS.SSN, '', true),
      reTypeSSN: this.formCreationService.getFormControl(FIELD_VALIDATIONS_CONSTANTS.SSN, '', true),
    });
    return identificationFormGrp;
  }

  /**
   * Get the regex for phone validation
   * @returns string
   */
  public getPhoneRegex(): string {
    return this.commonApiService.getFieldValidation(FIELD_VALIDATIONS_CONSTANTS.CHECK_NUMBER).regex;
  }

  /**
   * Builds the Communication form group for the credit check component
   * @returns FormGroup
   */
  public buildCommunicationForm(email: string): UntypedFormGroup {
    const communicationFormGrp = this.formBuilder.group({
      emailAddress: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.EMAIL_ADDRESS,
        email,
        true
      ),
      contactNumber: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.PHONE_NUMBER,
        '',
        true
      ),
    });
    return communicationFormGrp;
  }

  /**
   * Gets the account sub type in an array
   * @returns SelectItem[]
   */
  public getSelectItemArray(): SelectItem[] {
    const accountSubTypeOptions: SelectItem[] = [];
    for (const item of this.accountSubTypes) {
      accountSubTypeOptions.push({ label: item.displayValue, value: item.backendValue });
    }
    return accountSubTypeOptions;
  }

  /**
   * Gets the Customer's email address if available
   */
  public getEmailAddress(): string {
    const configData = this.stateStoreUtilsService.getCustomerConfigData();
    const customerEmail = configData && configData.email ? configData.email : '';
    return customerEmail;
  }
  /**
   * Post Credit check API call
   * @param form
   * @param addressValidation response of address api
   * @returns observable response for API call
   */
  public postCreditCheck(
    form: UntypedFormGroup,
    addressValidation: Address
  ): Observable<CreditCheckResponse> {
    const nameAddressFormValue = form.controls.nameAddressForm.value;
    const identificationForm = form.controls.identificationForm.value;
    const communicationForm = form.controls.communicationForm.value;
    const birthDate = this.datePipe.transform(identificationForm.birthDate, 'yyyy-dd-MM');
    const request = new CreditCheckRequestModel(
      nameAddressFormValue,
      identificationForm,
      communicationForm,
      this.objectService.get(addressValidation, 'addressid'),
      birthDate
    );
    return this.apiGatewayService
      .postRequest(API_MAP.API_2001_creditCheck, request, null, null, null)
      .pipe(
        map((response: HttpResponse<CreditCheckResponse>) => {
          this.logger.debug(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            'received response for postCreditCheck() in ' +
              CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
              ' response ' +
              JSON.stringify(response)
          );
          return response.body;
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            ' API error for postRequest for postCreditCheck() in ' +
              CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );
          return observableThrowError(error);
        })
      );
  }

  /**
   * Validates the address entered by the user
   * @param form
   * @returns an observable of the response received from api call
   */
  public validateAddress(
    form: UntypedFormGroup,
    isPayment?: boolean,
    zip?: string
  ): Observable<Address[]> {
    const headers = new HttpHeaders();
    const request = this.getAddressVerificationRequest(form, isPayment, zip);

    return this.apiGatewayService
      .postRequest(API_MAP.API_2004_addressValidation, request, headers, null, null)
      .pipe(
        map((response: HttpResponse<AddressValidationResponse>) => {
          this.logger.debug(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            'leaving validateAddress() in ' + CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE
          );
          return this.filterSuggestedAddress(response.body);
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            ' API error for postRequest for validateAddress() in ' +
              CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );
          return observableThrowError(error);
        })
      );
  }
  /**
   * Method to get form field(s) validators
   * @param fieldType
   */
  public getValidators(fieldType: string): ValidatorFn[] {
    return this.formCreationService.getFormControlValidator(fieldType, true);
  }

  /** Get AEM authorization content data for credit check pages
   * @returns AEM response
   * @param contentUrl
   * @param isverifyIdentity
   */
  public getContentData(isverifyIdentity?: boolean): Observable<object> {
    return this.contentProviderService.getContentData(null, 'postpaid').pipe(
      map((contentData: object) => {
        this.logger.debug(
          CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
          CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
          'received content data for getContentData() in  ' +
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
            ' contentData : ' +
            JSON.stringify(contentData)
        );
        if (isverifyIdentity) {
          this.setCreditCheckAuthorableData(contentData);
          return this.contentProviderService.getConfigurableData(contentData);
        } else {
          return this.contentProviderService.getConfigurableData(contentData);
        }
      })
    );
  }

  /**
   * API request for 2003
   * @param requestPayload API values populated for API 2003
   * @returns  Observable<any>
   */
  public postEmailValidation(requestObj: API2003Request): Observable<API2003Response> {
    return this.apiGatewayService
      .postRequest(ASSISTED_API_MAP.API_2003_postEmailValidation, requestObj, null, null)
      .pipe(
        map((response: HttpResponse<API2003Response>) => {
          this.logger.debug(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            'received response for API2003  ' +
              CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
              ' response : ' +
              JSON.stringify(response)
          );
          return response.body;
        }),
        catchError((error: APIError) => {
          this.logger.error(
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
            CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
            ' API error for postRequest for postEmailValidation() in ' +
              CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
              ' error: ' +
              JSON.stringify(error),
            error.code
          );
          return observableThrowError(error);
        })
      );
  }

  /**
   *
   * Retrieves the fieldValidations constants from common API service
   */
  public getFieldValidationConstants(): ObjectInterface {
    return this.commonApiService.getFieldValidationConstants();
  }

  /**
   *
   * creating form for credit check communication component
   */
  public electronicCommunicationForm(): UntypedFormGroup {
    const fieldValidationConstants = this.getFieldValidationConstants();
    this.logger.debug(
      CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
      CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
      'leaving electronicCommunicationForm() in ' +
        CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE
    );
    return this.formBuilder.group({
      email: this.formCreationService.getFormControl(
        fieldValidationConstants.EMAIL_ADDRESS,
        '',
        true
      ),
      confirmEmail: this.formCreationService.getFormControl(
        fieldValidationConstants.EMAIL_ADDRESS,
        '',
        true
      ),
      ageEligibility: ['', Validators.required],
    });
  }

  /**
   *
   * Used to create static modal
   */
  public cancelCreditCheckModal(contentdata: ConfigData = {}): StaticModal {
    const creditCheckmodal: StaticModal = {
      headerText: contentdata.dontRunMyCreditNotification,
      bodyText: contentdata.creditCheckMessage,
      disableDismiss: false,
      disableEsc: true,
      modalName: 'credit-check-modal',
      buttons: [
        {
          name: contentdata.shopPrepaidPlan,
          id: 'shopPrepaidBtn',
          class: 'btn btn-secondary',
          type: 'secondary',
        },
        {
          name: contentdata.noIWantToContinue,
          id: 'returnToECBtn',
          class: 'btn btn-primary',
          type: 'submit',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
    };
    this.logger.debug(
      CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
      CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
      'leaving cancelCreditCheckModal() in ' +
        CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE +
        ' returning param creditCheckmodal: ' +
        JSON.stringify(creditCheckmodal)
    );

    return creditCheckmodal;
  }

  /** Navigate to previous screen
   * @returns void
   */
  public navigateToPreviousScreen(): void {
    this.customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
    if (this.customerConfigData) {
      this.customerConfigData.zip = '';
      this.customerConfigData.billingType = BillingTypeEnum.PREPAID;
    }
    this.stateStoreUtilsService.setCustomerConfigData(this.customerConfigData);
    const url = this.stateStoreUtilsService.getPreviousUrl();
    this.router.navigate([url]);
    this.logger.debug(
      CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_MODULE_NAME,
      CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE,
      'leaving navigateToPreviousScreen() in ' + CREDIT_CHECK_MODULE_COMPONENTS.CREDIT_CHECK_SERVICE
    );
  }

  public populateSelectedAddress(selectedAddress: Address): void {
    this.selectedAddress.next(selectedAddress);
  }

  /**
   * Method to update Customer information in Config Data after Credit check is performed
   * @param form : Form which will be filled during Credit Check flow
   * @returns void
   */
  public updateCustomerConfigData(form: UntypedFormGroup): void {
    const nameAddressForm = 'nameAddressForm';
    const communicationForm = 'communicationForm';
    const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
    customerConfigData.firstName = form.controls[nameAddressForm].get('firstName').value;
    customerConfigData.lastName = form.controls[nameAddressForm].get('lastName').value;
    customerConfigData.middleName = form.controls[nameAddressForm].get('middleName').value;
    customerConfigData.personalContactNumber =
      form.controls[communicationForm].get('contactNumber').value;
    this.stateStoreUtilsService.setCustomerConfigData(customerConfigData);
  }

  /** Get address verification request
   * @returns AddressValidationRequest
   */
  private getAddressVerificationRequest(
    form: UntypedFormGroup,
    isPayment?: boolean,
    zip?: string
  ): AddressValidationRequest {
    if (!isPayment) {
      return {
        addressLine1: this.objectService.get(
          form,
          `controls.nameAddressForm.controls.addressLine1.value`
        ),
        addressLine2: this.objectService.get(
          form,
          `controls.nameAddressForm.controls.addressLine2.value`
        ),
        cityName: this.objectService.get(form, `controls.nameAddressForm.controls.city.value`),
        stateCode: this.objectService.get(form, `controls.nameAddressForm.controls.state.value`),
        postalCode:
          this.objectService.get(form, `controls.nameAddressForm.controls.zip.value`) ||
          this.objectService.get(form, `controls.zip.value`) ||
          zip,
      };
    } else {
      const nameAddressForm = 'address';
      const payment = 'payment';
      return {
        addressLine1: form.controls[nameAddressForm].get('address1').value,
        addressLine2: form.controls[nameAddressForm].get('address2').value,
        cityName: form.controls[nameAddressForm].get('city').value,
        stateCode: form.controls[nameAddressForm].get('state').value,
        postalCode: form.controls[payment].get('zipCode').value,
      };
    }
  }

  /** set credit check authorable data
   * @returns void
   */
  private setCreditCheckAuthorableData(data: object): void {
    this.accountSubTypes = <AemAuthorableContent[]>(
      this.contentProviderService.getAuthorableData(data, 'dropdown', 'accountSubType')
    );
    this.suffixes = <AemAuthorableContent[]>(
      this.contentProviderService.getAuthorableData(data, 'dropdown', 'suffix')
    );
  }

  /**
   * Function filters suggested addresses before returning data. The original address is identified by
   * address1.overrideIndicator === 'true', hence it would sort to the first value on the list
   * @param addresses All suggested addresses from API_2004 response
   * @returns SuggestedAddress as an array object
   */
  private filterSuggestedAddress(address: AddressValidationResponse): Address[] {
    return address.addresses.sort((address1: Address, address2: Address) => {
      // If address1 has overrideIndicator === 'true', then it should come first
      if (address1.overrideIndicator === 'true') {
        return address2.overrideIndicator === 'true' ? 0 : -1;
      } else {
        return address2.overrideIndicator === 'true' ? 1 : 0;
      }
    });
  }
}
